import React from 'react';
import {
  Play,
  Stop,
  RocketLaunch,
  CircleNotch,
  CheckCircle,
  SealCheck,
} from '@phosphor-icons/react';
import { isWorkflowEligible } from '@chatsdet/types';
import { isExpectedClientApiError } from '../../../../lib/api-error';

type LifecycleToast = {
  success: (message: string) => void;
  error: (message: string, options?: { reportError?: boolean }) => void;
};

function showLifecycleError(toast: LifecycleToast, message: string, error: unknown) {
  if (isExpectedClientApiError(error)) {
    toast.error(message, { reportError: false });
    return;
  }
  toast.error(message);
}

/**
 * The Flow Designer header carries TWO independent CTAs:
 *
 * - The **run button** (always shown) — run/stop execution, with a dropdown of
 *   run options. Morphs idle → prevalidating → running. Never vibrant.
 * - The **lifecycle button** (shown only when actionable) — the approval/
 *   promotion milestone CTA in the vibrant gradient.
 *
 * The behavior of this hook depends on `eligibilityModel` (per-org
 * `workflowEligibility` feature flag):
 *
 * - **Flag ON (eligibility model)**: suite inclusion is **derived eligibility** — a
 *   flow runs in suites once it is approved AND has passed at least once
 *   ({@link isWorkflowEligible}), unless paused (`pausedAt`) or degraded. The
 *   lifecycle button only nudges the one manual milestone left: approval. Pausing is
 *   a standalone toggle (`handlePause`/`handleUnpause`).
 * - **Flag OFF (promotion model)**: suite inclusion is a manual `promote`/`pause`
 *   action. Two axes drive the lifecycle button — approval (`status`) and promotion
 *   (`null | promoted | paused`). `promote` shows for approved-but-not-promoted flows
 *   with a passing run; force-promote bypasses the run gate.
 *
 * In both models the `approve` CTA is gated only on `isApprovable` (a settled
 * draft with content) — approval is a spec-review judgment and does NOT require
 * a passing run. Only suite inclusion (eligibility/promotion) keeps the run gate.
 */
export type RunState = 'idle' | 'prevalidating' | 'running';
export type LifecycleState =
  | 'hidden'
  | 'approve'
  | 'approving'
  | 'promote'
  | 'promoting'
  | 'promoted';

export type LifecycleButtonStateConfig = {
  label: string;
  icon: React.ReactNode;
  variant: 'primary' | 'destructive' | 'vibrant';
  iconAnimation?: 'spin' | 'pulse';
};

const RUN_BUTTON_STATES = {
  idle: {
    label: 'Run Flow',
    icon: <Play size={18} weight="fill" />,
    variant: 'primary' as const,
  },
  prevalidating: {
    label: 'Validating…',
    icon: <CircleNotch size={18} weight="bold" />,
    variant: 'primary' as const,
    iconAnimation: 'spin' as const,
  },
  running: {
    label: 'Stop Run',
    icon: <Stop size={18} weight="fill" />,
    variant: 'destructive' as const,
  },
} satisfies Record<RunState, LifecycleButtonStateConfig>;

const LIFECYCLE_BUTTON_STATES = {
  approve: {
    label: 'Approve Flow',
    icon: <SealCheck size={18} weight="fill" />,
    variant: 'vibrant' as const,
    iconAnimation: 'pulse' as const,
  },
  approving: {
    label: 'Approving…',
    icon: <CircleNotch size={18} weight="bold" />,
    variant: 'vibrant' as const,
    iconAnimation: 'spin' as const,
  },
  promote: {
    label: 'Promote Flow',
    icon: <RocketLaunch size={18} weight="fill" />,
    variant: 'vibrant' as const,
    iconAnimation: 'pulse' as const,
  },
  promoting: {
    label: 'Promoting…',
    icon: <CircleNotch size={18} weight="bold" />,
    variant: 'vibrant' as const,
    iconAnimation: 'spin' as const,
  },
  promoted: {
    label: 'Promoted',
    icon: <CheckCircle size={18} weight="fill" />,
    variant: 'vibrant' as const,
  },
} satisfies Record<Exclude<LifecycleState, 'hidden'>, LifecycleButtonStateConfig>;

type UseLifecycleControlsOptions = {
  workflowId: string | null;
  isRunning: boolean;
  isPrevalidating?: boolean;
  /**
   * When true, the org is on the eligibility model (flag ON): suite inclusion is
   * derived (approved + ever-passed) and the lifecycle button only handles approval.
   * When false, the legacy promotion model is used.
   */
  eligibilityModel: boolean;
  /** Approval axis: status === 'approved'. */
  isApproved: boolean;
  /**
   * Whether the flow is a settled, reviewable draft (status `draft` with at
   * least one node). Gates the approve CTA — approval never requires a run.
   */
  isApprovable: boolean;
  // --- Eligibility model (flag ON) inputs ---
  /** Write-once "has ever passed" timestamp — with approval derives eligibility. */
  firstPassedAt: string | null;
  /** Manual pause toggle (set = paused, excluded from suite runs). */
  isPaused: boolean;
  unpauseFlow: () => Promise<void>;
  // --- Promotion model (flag OFF) inputs ---
  /** Suite-inclusion axis: null = never promoted. */
  promotion: 'promoted' | 'paused' | null;
  /** Run gate for promotion (server column readyToPromote). */
  readyToPromote: boolean;
  isPromoting: boolean;
  promoteFlow: (options?: { force?: boolean }) => Promise<void>;
  // --- Shared inputs ---
  canRunFlow: boolean;
  isApproving: boolean;
  runFlow: () => Promise<void>;
  stopRun: () => void;
  approveFlow: () => Promise<void>;
  pauseFlow: () => Promise<void>;
  /** Pressing the Approve CTA opens the two-step approval dialog (owned by the page). */
  onApprovePress: () => void;
  onRunCompleted?: () => void;
  onLifecycleChanged?: () => Promise<void> | void;
  toast: LifecycleToast;
};

export const useLifecycleControls = (options: UseLifecycleControlsOptions) => {
  const {
    workflowId,
    isRunning,
    isPrevalidating,
    eligibilityModel,
    isApproved,
    isApprovable,
    firstPassedAt,
    isPaused,
    unpauseFlow,
    promotion,
    readyToPromote,
    isPromoting,
    promoteFlow,
    canRunFlow,
    isApproving,
    runFlow,
    stopRun,
    approveFlow,
    pauseFlow,
    onApprovePress,
    onRunCompleted,
    onLifecycleChanged,
    toast,
  } = options;
  const [recentlyPromoted, setRecentlyPromoted] = React.useState(false);
  const prevIsRunningRef = React.useRef(isRunning);

  React.useEffect(() => {
    if (prevIsRunningRef.current && !isRunning && workflowId) {
      onRunCompleted?.();
    }
    prevIsRunningRef.current = isRunning;
  }, [isRunning, workflowId, onRunCompleted]);

  React.useEffect(() => {
    if (!recentlyPromoted) {
      return;
    }
    const timeout = window.setTimeout(() => setRecentlyPromoted(false), 2500);
    return () => window.clearTimeout(timeout);
  }, [recentlyPromoted]);

  /**
   * Derived suite-inclusion eligibility (approved + has-passed), plus a human
   * reason describing what still gates a flow from running in suites. Used by the
   * eligibility model (flag ON).
   */
  const isEligible = isWorkflowEligible({
    status: isApproved ? 'approved' : 'draft',
    firstPassedAt,
  });
  const eligibilityReason = !isApproved
    ? 'Needs approval'
    : firstPassedAt == null
      ? "Hasn't passed yet"
      : 'Eligible';

  /**
   * Approve the flow. Returns true on success so the two-step dialog can advance
   * to its "Hooray" step. No toast on success — the dialog IS the feedback.
   */
  const handleApprove = React.useCallback(async (): Promise<boolean> => {
    if (!workflowId) {
      return false;
    }
    try {
      await approveFlow();
      await onLifecycleChanged?.();
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to approve flow';
      showLifecycleError(toast, message, error);
      return false;
    }
  }, [workflowId, approveFlow, toast, onLifecycleChanged]);

  // Promotion model (flag OFF): manual promote / force-promote.
  const handlePromote = React.useCallback(
    async (promoteOptions?: { force?: boolean }) => {
      if (!workflowId) {
        return;
      }
      try {
        await promoteFlow(promoteOptions);
        setRecentlyPromoted(true);
        toast.success('Flow promoted — it will run when test suites run');
        await onLifecycleChanged?.();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to promote flow';
        showLifecycleError(toast, message, error);
      }
    },
    [workflowId, promoteFlow, toast, onLifecycleChanged]
  );

  const handleForcePromote = React.useCallback(async () => {
    await handlePromote({ force: true });
  }, [handlePromote]);

  const handlePause = React.useCallback(async () => {
    if (!workflowId) {
      return;
    }
    try {
      await pauseFlow();
      toast.success(
        eligibilityModel
          ? 'Flow paused — excluded from suite runs'
          : 'Flow paused — excluded from suite runs until re-promoted'
      );
      await onLifecycleChanged?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to pause flow';
      showLifecycleError(toast, message, error);
    }
  }, [workflowId, pauseFlow, toast, onLifecycleChanged, eligibilityModel]);

  const handleUnpause = React.useCallback(async () => {
    if (!workflowId) {
      return;
    }
    try {
      await unpauseFlow();
      toast.success('Flow unpaused — it will run when test suites run');
      await onLifecycleChanged?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to unpause flow';
      showLifecycleError(toast, message, error);
    }
  }, [workflowId, unpauseFlow, toast, onLifecycleChanged]);

  // Run button: run/stop execution only.
  const runState: RunState = React.useMemo(() => {
    if (isRunning) {
      return 'running';
    }
    if (isPrevalidating) {
      return 'prevalidating';
    }
    return 'idle';
  }, [isRunning, isPrevalidating]);

  // Lifecycle button: approval/promotion milestone CTA.
  const lifecycleState: LifecycleState = React.useMemo(() => {
    if (isApproving) {
      return 'approving';
    }
    if (eligibilityModel) {
      // Eligibility model: only ever nudge approval; suite inclusion is derived.
      if (isApprovable && !isApproved) {
        return 'approve';
      }
      return 'hidden';
    }
    // Promotion model: approve then promote. Only promote keeps the run gate.
    if (isPromoting) {
      return 'promoting';
    }
    if (recentlyPromoted) {
      return 'promoted';
    }
    if (isApprovable && !isApproved) {
      return 'approve';
    }
    if (readyToPromote && isApproved && promotion !== 'promoted') {
      return 'promote';
    }
    return 'hidden';
  }, [
    isApproving,
    eligibilityModel,
    isApprovable,
    isApproved,
    isPromoting,
    recentlyPromoted,
    readyToPromote,
    promotion,
  ]);

  const isRunDisabled = React.useMemo(() => {
    if (runState === 'prevalidating') {
      return true;
    }
    return runState === 'idle' && !canRunFlow;
  }, [runState, canRunFlow]);

  const isLifecycleDisabled = lifecycleState === 'approving' || lifecycleState === 'promoting';

  const handleRunPress = React.useCallback(async () => {
    if (runState === 'running') {
      stopRun();
      return;
    }
    if (runState === 'prevalidating') {
      return;
    }
    await runFlow();
  }, [runState, runFlow, stopRun]);

  const handleLifecyclePress = React.useCallback(async () => {
    if (lifecycleState === 'approve') {
      onApprovePress();
      return;
    }
    if (lifecycleState === 'promote') {
      await handlePromote();
      return;
    }
    // approving / promoting / promoted / hidden — no-op.
  }, [lifecycleState, handlePromote, onApprovePress]);

  return {
    runState,
    lifecycleState,
    runButtonStates: RUN_BUTTON_STATES,
    lifecycleButtonStates: LIFECYCLE_BUTTON_STATES,
    isRunDisabled,
    isLifecycleDisabled,
    isEligible,
    eligibilityReason,
    isPaused,
    handleRunPress,
    handleLifecyclePress,
    handleApprove,
    handlePromote,
    handleForcePromote,
    handlePause,
    handleUnpause,
  } as const;
};
